import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@fragments";
import { IconArrowLeft, IconUserPlus, IconUpload, IconSettings } from "@tabler/icons-react";

import useEnrollments from "./hooks/useEnrollments";
import MentoringEnrollmentsView from "./view";
import ProgramEditorModal from "../view/components/ProgramEditorModal";
import InviteModal from "../view/components/InviteModal";
import ImportModal from "../view/components/ImportModal";
import EnrollmentActionModal from "./components/EnrollmentActionModal";

/**
 * Program detail + enrollments list — §6.3 (subtab = programId).
 * Header actions: back · Convidar (§6.5) / Importar (§6.6) · Editar programa
 * (reuses the stage-2 editor). Row review/accept/reject (§6.7) and end (§6.8)
 * flow through the shared EnrollmentActionModal. Row click → detail (§6.4).
 */
export default function MentoringEnrollments({ setPageActions }) {
  const navigate = useNavigate();
  const { slug, subtab: programId } = useParams();
  const state = useEnrollments(programId);
  const editorRef = useRef({});
  const inviteRef = useRef({});
  const importRef = useRef({});
  const actionRef = useRef({});

  const { program, currentCommunity, applyProgramUpdate, refresh } = state;
  const communityId = currentCommunity?.id;

  const onInvite = useCallback(() => {
    inviteRef.current.open?.(program, refresh);
  }, [program, refresh]);

  const onImport = useCallback(() => {
    importRef.current.open?.(program, refresh);
  }, [program, refresh]);

  const onEnrollmentAction = useCallback(
    (enrollment, action) => {
      actionRef.current.open?.({ enrollment, action, onDone: refresh });
    },
    [refresh],
  );

  useEffect(() => {
    if (!setPageActions) return;
    setPageActions(
      <div style={{ display: "flex", alignItems: "center", gap: "var(--gap-1)" }}>
        <Button text="Voltar" startIcon={<IconArrowLeft />} color="overlay" size="small" onClick={() => navigate(`/${slug}/settings/mentoring`)} />
        <Button text="Convidar" startIcon={<IconUserPlus />} color="overlay" size="small" disabled={!program} onClick={onInvite} />
        <Button text="Importar" startIcon={<IconUpload />} color="overlay" size="small" disabled={!program} onClick={onImport} />
        <Button text="Editar programa" startIcon={<IconSettings />} color="primary" size="small" disabled={!program} onClick={() => editorRef.current.open?.(program, applyProgramUpdate)} />
      </div>
    );
    return () => setPageActions(null);
  }, [setPageActions, slug, navigate, program, applyProgramUpdate, onInvite, onImport]);

  return (
    <>
      <MentoringEnrollmentsView
        {...state}
        onInvite={onInvite}
        onEnrollmentAction={onEnrollmentAction}
      />
      <ProgramEditorModal modalRef={editorRef} community={currentCommunity} />
      <InviteModal modalRef={inviteRef} communityId={communityId} />
      <ImportModal modalRef={importRef} communityId={communityId} />
      <EnrollmentActionModal modalRef={actionRef} communityId={communityId} />
    </>
  );
}
